import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Calendar, momentLocalizer } from "react-big-calendar";
import { withTranslation } from "react-i18next";
import { Button, Grid } from "@mui/material";
import moment from "moment";
import { useFormik } from "formik";
import { uniqueId } from "lodash";
import * as Yup from "yup";
import PropTypes from "prop-types";

import Modal from "Components/Common/Modal";
import SelectField from "Components/Common/SelectField";
import ConfirmationDialog from "Components/Common/ConfirmationDialog";
import InputFieldText from "Components/Common/InputFieldText";
import Backdrop from "Components/Common/Backdrop";
import {
  FIRE_SAFETY_CONSUMER,
  fireSafetyAdminRoles,
  fireSafetyScheduleStatusTypes,
} from "common/constant";

import { filterEmptyStrings, isAuthorizedUser } from "utils";
import { convertUTCDateToLocalDate } from "utils/convertUTCDateToLocalDate";

import {
  useDeleteScheduleMutation,
  useGetDefaultSchedulerQuery,
  useGetDeveloperBuildingsQuery,
} from "apis/AuditSchedular";

import "react-big-calendar/lib/css/react-big-calendar.css";
import CustomEvent from "./CustomEvent";

const eventColors = {
  consumer: "#d96e66",
  reschedule: "#abad2a",
  default: "#3174ad",
};

const otherBuildingId = 0;

const getEndDate = (date) =>
  moment(date).subtract("1", "day").format("YYYY-MM-DD");

const getStartDate = (date) => moment(date).format("YYYY-MM-DD");

const CalenderView = ({
  t,
  events = [],
  onDateChange,
  buildingName,
  isCalendarScreen = false,
}) => {
  const { roles } = useSelector((state) => state.Login);

  const {
    data: { data: buildingsData } = {},
    isLoading: isBuildingDataLoading,
  } = useGetDeveloperBuildingsQuery();

  const [deleteScheduler, { isLoading: isSchedulerDeleting }] =
    useDeleteScheduleMutation();

  const { data: { data: defaultSchedularData } = {}, isLoading } =
    useGetDefaultSchedulerQuery(
      filterEmptyStrings({
        buildingName: roles.includes(FIRE_SAFETY_CONSUMER) ? buildingName : "",
      })
    );

  const [selectedSlotDetails, setSelectedSlotDetails] = useState(null);
  const [selectedDeleteEventProps, setSelectedDeleteEventProps] =
    useState(null);
  const [currentSelectedDate, setCurrentSelectedDate] = useState(new Date());
  const [calenderEvents, setCalenderEvents] = useState([]);
  const [isDateDeleted, setIsDateDeleted] = useState(false);

  const formik = useFormik({
    initialValues: { buildingId: "" },
    validationSchema: Yup.object({
      buildingId: Yup.string().required("Building is required"),
      // otherBuildingName: Yup.string().test(
      //   "isOtherBuildingNameRequired",
      //   "",
      //   function () {
      //     const [{ value: current = null } = {}] = this.from || [];

      //     if (
      //       current.buildingId === otherBuildingId &&
      //       !current.otherBuildingName
      //     ) {
      //       return this.createError({
      //         message: "Building name is required",
      //       });
      //     }

      //     if (
      //       current.buildingId === otherBuildingId &&
      //       buildingsData?.some(
      //         ({ name }) =>
      //           name.toLowerCase() === current.otherBuildingName.toLowerCase()
      //       )
      //     ) {
      //       return this.createError({
      //         message: "Building name should be unique",
      //       });
      //     }
      //     return true;
      //   }
      // ),
    }),

    onSubmit: ({ buildingId, otherBuildingName }, { resetForm }) => {
      const id = uniqueId();
      if (onDateChange) {
        onDateChange((prev) => [
          ...prev,
          {
            id,
            startDate: selectedSlotDetails
              ? moment(selectedSlotDetails.start).format("YYYY-MM-DD")
              : "",
            endDate: selectedSlotDetails
              ? moment(selectedSlotDetails.end)
                  .subtract(1, "day")
                  .format("YYYY-MM-DD")
              : "",
            buildingName: buildingId || otherBuildingName,
          },
        ]);
      }
      setCalenderEvents((prev) => [
        ...prev,
        {
          id,
          start: selectedSlotDetails.start,
          end: selectedSlotDetails.end,
          title: buildingId || otherBuildingName,
          type: "reschedule",
        },
      ]);
      setSelectedSlotDetails(null);
      resetForm();
    },
  });

  const {
    handleSubmit,
    resetForm,
    setFieldValue,
    values: { buildingId },
  } = formik;

  const updatedBuildingOptions = useMemo(
    () => [
      ...(buildingsData?.map(({ name }) => ({ label: name, value: name })) ??
        []),
      // { value: 0, label: "Other" },
    ],
    [buildingsData]
  );

  useEffect(() => {
    if (defaultSchedularData?.length) {
      setCalenderEvents([
        ...defaultSchedularData.map(
          ({ id, developerBuilding, startDate, endDate, status }) => ({
            id,
            title: developerBuilding.name,
            start: new Date(startDate),
            status,
            end: new Date(moment(endDate).add(1).format("YYYY-MM-DD")),
          })
        ),
        ...events.map((item) => ({
          ...item,
          end:
            item.type !== "consumer"
              ? moment(item.end).add(1, "day")
              : moment(item.end),
        })),
      ]);

      if (!isDateDeleted) {
        const incompleteSchedule = defaultSchedularData.find(
          ({ developerBuilding, status }) =>
            developerBuilding.name === buildingName &&
            status !== fireSafetyScheduleStatusTypes["Completed"]
        );

        const lastScheduleStartDate =
          defaultSchedularData[defaultSchedularData.length - 1]?.startDate;

        const defaultDate = roles.includes(FIRE_SAFETY_CONSUMER)
          ? lastScheduleStartDate
          : moment();

        const selectedDate = convertUTCDateToLocalDate(
          incompleteSchedule?.startDate ?? defaultDate
        ).format("YYYY-MM-DD");

        setCurrentSelectedDate(selectedDate);
      }
    }
  }, [defaultSchedularData, isDateDeleted]);

  const geIsSelectedSlots = (date) =>
    calenderEvents.some(({ start, end, type }) => {
      const startDate = moment(start).format("YYYY-MM-DD");
      const endDate =
        type === "reschedule"
          ? moment(end).subtract(1, "day").format("YYYY-MM-DD")
          : moment(end).format("YYYY-MM-DD");
      return (
        moment(date).isBetween(startDate, endDate) ||
        moment(date).isSame(startDate) ||
        moment(date).isSame(endDate)
      );
    });

  const getIsSlotSelectable = (startDate, endDate) =>
    !geIsSelectedSlots(startDate) &&
    !geIsSelectedSlots(endDate) &&
    moment(startDate).isSame(currentSelectedDate, "month") &&
    moment(endDate).isSame(currentSelectedDate, "month");

  const deleteBtnHandler = async () => {
    if (!selectedDeleteEventProps.type) {
      try {
        await deleteScheduler(selectedDeleteEventProps.id);
        if (buildingName === selectedDeleteEventProps.title) {
          setIsDateDeleted(true);
          setCurrentSelectedDate(moment());
        }
      } catch (error) {}
    } else {
      onDateChange((prev) =>
        prev.filter(({ id }) => id !== selectedDeleteEventProps.id)
      );
      setCalenderEvents((prev) =>
        prev.filter(({ id }) => id !== selectedDeleteEventProps.id)
      );
    }
    setSelectedDeleteEventProps(null);
  };

  const onSlotSelect = (slotInfo) => {
    const startDate = getStartDate(slotInfo.start);
    const endDate = getEndDate(slotInfo.end);

    if (
      !calenderEvents.some(
        ({ start, end }) =>
          slotInfo.slots?.some((date) => moment(date).isSame(start)) ||
          slotInfo.slots?.some((date) => moment(date).isSame(end))
      ) &&
      getIsSlotSelectable(startDate, endDate) &&
      moment(slotInfo.start).isSame(currentSelectedDate, "month") &&
      moment(endDate).isSame(currentSelectedDate, "month") &&
      moment(startDate).isSameOrAfter(moment(), "date")
    ) {
      setSelectedSlotDetails({
        ...slotInfo,
        start: moment(slotInfo.start).format("YYYY-MM-DD"),
        end: moment(slotInfo.end).format("YYYY-MM-DD"),
      });
    }
  };

  if (isBuildingDataLoading || isLoading) {
    return <Backdrop open />;
  }

  return (
    <>
      <ConfirmationDialog
        message="Are you sure you want to delete this schedule?"
        onCancel={() => setSelectedDeleteEventProps(null)}
        onConfirm={deleteBtnHandler}
        isOpen={!!selectedDeleteEventProps}
        title="Warning"
        variant="warning"
        isLoading={isSchedulerDeleting}
      />

      {!!selectedSlotDetails && (
        <Modal
          isOpen
          handleClose={() => {
            setSelectedSlotDetails(null);
            resetForm();
          }}
          maxWidth="sm"
          title="Enter Building Name"
        >
          <Grid container className="mt-3" columnSpacing={2}>
            <Grid item xs={12}>
              <SelectField
                label={t("Building") + "*"}
                name="buildingId"
                onChange={(_, { value }) => {
                  setFieldValue("buildingId", value);
                  setFieldValue("otherBuildingName", "");
                }}
                formik={formik}
                options={updatedBuildingOptions || []}
              />
            </Grid>
            {buildingId === otherBuildingId && (
              <Grid item xs={12}>
                <InputFieldText
                  label={t("Building Name") + "*"}
                  name="otherBuildingName"
                  formik={formik}
                  placeholder="Enter Building Name"
                />
              </Grid>
            )}
            <Grid item xs={12} className="d-flex justify-content-end">
              <Button size="medium" variant="contained" onClick={handleSubmit}>
                {t("Save")}
              </Button>
            </Grid>
          </Grid>
        </Modal>
      )}

      <Calendar
        selectable={
          !isCalendarScreen && isAuthorizedUser(roles, fireSafetyAdminRoles)
        }
        style={{ height: "700px" }}
        popup
        events={calenderEvents}
        startAccessor="start"
        endAccessor="end"
        localizer={momentLocalizer(moment)}
        onSelectEvent={onSlotSelect}
        components={{
          event: (e) => (
            <CustomEvent
              event={e.event}
              buildingName={buildingName}
              deleteBtnHandler={() =>
                setSelectedDeleteEventProps({
                  id: e.event.id,
                  type: e.event.type,
                  title: e.event.title,
                })
              }
            />
          ),
        }}
        date={currentSelectedDate}
        onSelectSlot={onSlotSelect}
        onNavigate={(date) => setCurrentSelectedDate(date)}
        eventPropGetter={({ type = "default", title, status }) => ({
          style: {
            backgroundColor:
              status === fireSafetyScheduleStatusTypes["Completed"]
                ? "#9c9c9c"
                : title === buildingName &&
                  !["reschedule", "consumer"].includes(type)
                ? "#728c58"
                : eventColors[type],
          },
        })}
        dayPropGetter={(date) => {
          return {
            style:
              geIsSelectedSlots(date) ||
              !moment(date).isSameOrAfter(moment(), "date")
                ? {
                    backgroundColor: "#f5f5f5",
                    cursor: "no-drop",
                    // background: "none",
                  }
                : { cursor: "pointer" },
          };
        }}
      />
    </>
  );
};

CalenderView.propTypes = {
  events: PropTypes.array,
  t: PropTypes.func,
  onDateChange: PropTypes.func,
  buildingName: PropTypes.string,
  isCalendarScreen: PropTypes.bool,
};

export default withTranslation()(CalenderView);
